import { useId } from 'react'

import { IconExternalLink } from '@tabler/icons-react'

import { Checkbox } from '@/components/sui/checkbox'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { getMarketingUrl } from '@/lib/urls'
import { useLanguageStore } from '@/stores/language'

import locales from './locales'

interface AffiliateTermsConsentProps {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
  className?: string
}

/**
 * The acceptance both doors into the program have to collect before joining:
 * the sidebar's activation dialog and the section's own landing. It is the
 * same consent either way, so the checkbox, the wording and the link to the
 * terms live here rather than being written twice.
 *
 * The id is generated per instance: both doors can be mounted at once (the
 * dialog opens over the landing), and a fixed one would point every label at
 * the first checkbox on the page.
 */
export function AffiliateTermsConsent({
  checked,
  onCheckedChange,
  disabled = false,
  className,
}: AffiliateTermsConsentProps) {
  const { t } = useComponentTranslation(locales)
  const locale = useLanguageStore((state) => state.locale)
  const id = useId()

  return (
    <div
      className={`flex cursor-pointer items-center space-x-2 ${className ?? ''}`}
    >
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        className="cursor-pointer"
        disabled={disabled}
      />
      <label
        htmlFor={id}
        className="cursor-pointer text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
      >
        {t('label')}{' '}
        <a
          href={getMarketingUrl('affiliateTerms', locale)}
          className="text-link inline-flex items-center gap-1 underline-offset-4 hover:underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('link')}
          <IconExternalLink className="size-3" />
        </a>
      </label>
    </div>
  )
}
